import { useCallback, useEffect, useRef, useState } from "react"
import { Table, Tooltip } from "antd"
import type { TableProps } from "antd"
import type { TablePaginationConfig } from "antd/es/table"
import { AppTableScrollbar } from "./AppTableScrollbar"
import { ACTION_COLUMN_TITLE, isActionColumn } from "./actionColumn"
import "./appTable.css"

/**
 * The standard list table.
 *
 * A thin wrapper over antd `<Table>` that carries the defaults ~160 tables were
 * copy-pasting: the pagination object, the scroll box and `tableLayout`. Every
 * other prop passes straight through, so anything antd supports still works and
 * a caller can override any default by passing it explicitly.
 *
 * It also carries the table design (see appTable.css). A single table can opt
 * out with `styleVariant="legacy"` if the new styling breaks it, rather than
 * reverting the change everywhere.
 *
 * Not to be confused with `new-stuff/data-table/DataTable`, which serves the
 * feature-rich payroll and report grids (freeze columns, column visibility, row
 * pinning, resizing). If a table needs those, use `DataTable`. If it is a
 * standard list, use this.
 */

/** Which edges have content hidden beyond them. */
export const resolveScrollEdges = ({
  scrollLeft,
  scrollWidth,
  clientWidth,
}: {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}) => {
  // Sub-pixel tolerance: browsers report fractional widths, and an exact
  // comparison leaves the bar flickering at the extremes.
  const max = scrollWidth - clientWidth
  const scrollable = max > 1
  return {
    canScrollLeft: scrollable && scrollLeft > 1,
    canScrollRight: scrollable && scrollLeft < max - 1,
  }
}

export const APP_TABLE_DEFAULT_PAGE_SIZE = 20
export const APP_TABLE_PAGE_SIZE_OPTIONS = [20, 40, 100, 500, 1000]

/**
 * Read antd's `loading` prop, which is `boolean | SpinProps`.
 *
 * Exported for the test: the object form is rare enough that a regression
 * here would only show up as a table that never stops loading.
 */
export const resolveLoading = (loading: unknown): boolean => {
  if (typeof loading === "object" && loading !== null) {
    const spinning = (loading as { spinning?: boolean }).spinning
    return spinning === undefined ? true : Boolean(spinning)
  }
  return Boolean(loading)
}

export const APP_TABLE_SCROLL: TableProps<any>["scroll"] = {
  y: "calc(100vh - 300px)",
  x: "max-content",
}

export const appTableShowTotal: TablePaginationConfig["showTotal"] = (
  total,
  range
) => (
  <span className="font-avenir text-padeSubtext grow">
    {range?.[0]}-{range?.[1]} of {total} items
  </span>
)

export const APP_TABLE_PAGINATION: TablePaginationConfig = {
  showTotal: appTableShowTotal,
  defaultPageSize: APP_TABLE_DEFAULT_PAGE_SIZE,
  pageSizeOptions: APP_TABLE_PAGE_SIZE_OPTIONS,
}

export type AppTableProps<T extends Record<string, any>> = Omit<
  TableProps<T>,
  "scroll"
> & {
  /**
   * `false` opts out of the default scroll box, for tables that should grow
   * with the page — print templates, tables inside modals and accordions.
   * Mirrors antd's own `pagination={false}`.
   */
  scroll?: TableProps<T>["scroll"] | false
  /**
   * `"legacy"` drops the design class, leaving antd's stock styling. An escape
   * hatch for a table the new look does not suit — not a long-term option.
   */
  styleVariant?: "default" | "legacy"
}

/**
 * Resolves the three defaulted props. Split out from the component so the
 * precedence rules can be unit tested.
 *
 * Only an *omitted* prop takes a default. That is what makes adopting AppTable
 * a no-op for a table that already sets these explicitly, so `false` has to
 * mean "none" rather than "give me the default".
 */
export const resolveAppTableDefaults = <T extends Record<string, any>>({
  pagination,
  scroll,
  tableLayout,
}: Pick<AppTableProps<T>, "pagination" | "scroll" | "tableLayout">) => ({
  pagination: pagination === undefined ? APP_TABLE_PAGINATION : pagination,
  scroll:
    scroll === undefined
      ? APP_TABLE_SCROLL
      : scroll === false
      ? undefined
      : scroll,
  tableLayout: tableLayout === undefined ? "auto" : tableLayout,
})

const FREEZE_DIVIDER_CLASSES = [
  "app-table-freeze-divider",
  "report-freeze-divider",
]

const withoutDivider = (className: unknown) =>
  String(className ?? "")
    .split(/\s+/)
    .filter((name) => name && !FREEZE_DIVIDER_CLASSES.includes(name))
    .join(" ")

/**
 * Below this width the action column cannot be hidden by horizontal scrolling,
 * so hoisting it only scrambles a column order people already know. Four
 * columns still fit comfortably at the 1440px the design targets.
 */
const MIN_COLUMNS_TO_HOIST_ACTION = 5

/**
 * The design puts a `Select action` column second, right after the identity
 * column, rather than trailing off the right-hand edge where a wide table hides
 * it. Done here so no call site has to reorder its own array.
 *
 * The hoist is conditional on the table actually being wide, because the reason
 * for it is that the action scrolls out of sight. A narrow table shows every
 * column at once — moving the action there buys nothing and costs the reader a
 * familiar layout, pushing whatever followed it (typically a status) out to the
 * right-hand edge, away from the row it describes.
 *
 * Icon-group action columns stay where they are: they read as a short row of
 * direct controls at the end of the row, not as a menu. Those columns opt out
 * with `actionPlacement: "end"`, which is explicit rather than inferred from
 * whatever their `render` happens to return.
 */
export const positionActionColumn = <
  C extends { key?: unknown; actionPlacement?: "start" | "end" }
>(
  columns: C[] | undefined
): C[] | undefined => {
  if (!columns?.length) return columns
  const index = columns.findIndex(
    (column) =>
      isActionColumn(column) && (column as any).actionPlacement !== "end"
  )
  if (index < 0) return columns

  const actionColumn = columns[index] as any
  const rest = columns.filter((_, i) => i !== index)

  // The left-frozen run the action must not break. antd requires fixed columns
  // to be contiguous from the edge, so a scrolling column dropped inside the run
  // detaches everything frozen after it.
  let frozenRun = 0
  while (frozenRun < rest.length && (rest[frozenRun] as any).fixed) frozenRun++

  let target: number
  if (actionColumn.fixed) {
    // Frozen: it joins the run in its design position, straight after the identity column.
    // Report tables lead the run with a pin and a row-number column, which are not the
    // identity, so "second" is counted from past them -- otherwise the action landed between
    // the pin and the row number.
    let lead = 0
    while (lead < frozenRun && isUtilityColumn(rest[lead])) lead++
    target = Math.min(lead + 1, frozenRun)
  } else if (frozenRun > 0) {
    // Not frozen while others are: it goes straight after the run and slides
    // under it, the same as it does with a single frozen column.
    target = frozenRun
  } else {
    // Nothing frozen. Too narrow for a hoist to be worth it.
    if (columns.length < MIN_COLUMNS_TO_HOIST_ACTION) return columns
    // "Second" means after the identity column. Where a row-number column
    // leads, identity is the one after it: dropping the action between the two
    // separated the name from its number, and freezeFirstColumn then pinned the
    // number and the action while the name scrolled away.
    target = rest.length > 1 && isSerialColumn(rest[0]) ? 2 : 1
  }
  if (target === index && actionColumn.title) return columns

  const next = [...rest]
  next.splice(target, 0, {
    ...actionColumn,
    // These columns are usually untitled; the design labels them.
    title: actionColumn.title || ACTION_COLUMN_TITLE,
  })

  // Moving a frozen action changes which column ends the run, so the seam has to
  // move with it or it is drawn in the middle of the frozen block.
  if (!actionColumn.fixed) return next as C[]
  const hadDivider = FREEZE_DIVIDER_CLASSES.find((name) =>
    next.some((column: any) =>
      String(column.className ?? "")
        .split(/\s+/)
        .includes(name)
    )
  )
  if (!hadDivider) return next as C[]
  const lastFrozen = next.reduce(
    (last, column: any, i) => (column.fixed ? i : last),
    -1
  )
  return next.map((column: any, i) => {
    if (!column.fixed) return column
    const base = withoutDivider(column.className)
    return {
      ...column,
      className:
        i === lastFrozen ? [base, hadDivider].filter(Boolean).join(" ") : base,
    }
  }) as C[]
}

const SERIAL_TITLES = new Set(["sn", "s/n", "#", "no", "no.", "serial", "s/no"])

/** Columns that lead a frozen run without being its identity: a row pin or a row number. */
const UTILITY_COLUMN_CLASSES = ["report-pin-cell", "report-serial-cell"]

const isUtilityColumn = (column: any) =>
  isSerialColumn(column) ||
  String(column?.className ?? "")
    .split(/\s+/)
    .some((name) => UTILITY_COLUMN_CLASSES.includes(name))

/**
 * Is this a row-number column rather than an identity one? Judged by title,
 * because these columns carry no meaningful key or dataIndex — the value is
 * rendered from the row index.
 */
const isSerialColumn = (column: any) => {
  const title = typeof column?.title === "string" ? column.title : ""
  return SERIAL_TITLES.has(title.trim().toLowerCase())
}

/**
 * Pins the leading identity column so it stays put while the rest slide under it, along with
 * the action column when it sits straight after it.
 *
 * Uses antd's own `fixed: "left"` plus a divider class for the seam, and pins
 * the selection checkbox alongside it — a checkbox that scrolls away from its
 * row is worse than not pinning at all.
 *
 * Where the table leads with a row-number column, that number is not the
 * identity — pinning it alone leaves "1, 2, 3" stranded while the name it
 * belongs to scrolls away, which is the one thing freezing exists to prevent.
 * The number cannot simply be skipped either: antd requires left-fixed columns
 * to be contiguous from the edge, and pinning column 1 while column 0 floats
 * renders them overlapping. So both are pinned. The serial column is narrow, so
 * the pair costs little width.
 *
 * No width is imposed. rc-table keeps `table-layout: auto` when `scroll.x` is
 * `max-content` (its own comment: the width should stretch to fit content), so
 * a forced width fights content sizing and the next column ends up rendering
 * underneath the sticky one. A width the call site set is respected.
 */
export const freezeFirstColumn = <C extends Record<string, any>>(
  columns: C[] | undefined
): C[] | undefined => {
  if (!columns?.length) return columns
  const [first] = columns
  // Respect a call site that already made its own decision.
  if (first.fixed) return columns

  // The seam belongs after the last pinned column, not after the row number.
  let lastPinned =
    isSerialColumn(first) && columns.length > 1 && !columns[1].fixed ? 1 : 0

  // The action the table placed beside the identity column is pinned with it. It was moved
  // there so a wide table could not scroll it out of sight, and a column that scrolls
  // away the moment the table moves defeats that.
  const afterIdentity = columns[lastPinned + 1]
  if (
    afterIdentity &&
    !afterIdentity.fixed &&
    isActionColumn(afterIdentity) &&
    afterIdentity.actionPlacement !== "end"
  ) {
    lastPinned += 1
  }

  return columns.map((column, index) => {
    if (index > lastPinned) return column
    return {
      ...column,
      fixed: "left",
      className:
        index === lastPinned
          ? [column.className, "app-table-freeze-divider"]
              .filter(Boolean)
              .join(" ")
          : column.className,
    }
  }) as C[]
}

/**
 * Beyond this the placeholder is off-screen anyway, and the page-size options
 * go up to 1000 - which would be a thousand shimmering rows nobody sees.
 */
export const APP_TABLE_MAX_SKELETON_ROWS = 50

/**
 * How many skeleton rows to draw while a table loads.
 *
 * Matched to the height the table is about to be, so nothing moves when the
 * rows arrive: a refresh redraws what is already on screen, and a first load
 * draws a full page. A table showing ten per page gets ten rows, one showing
 * twenty gets twenty.
 *
 * Both `pageSize` and `defaultPageSize` are read - antd accepts either, and
 * several call sites set only the latter, which used to fall through to the
 * default and draw the wrong height.
 */
/**
 * Turn one column into its loading placeholder, walking grouped columns.
 *
 * A grouped header carries its real columns in `children`, and a spread copies
 * those through untouched - so a leaf `render` still ran, against a skeleton
 * row that carries none of the fields a real row has. The payroll report
 * crashed on exactly that: `'Transport' in row.person_columns`, undefined.
 *
 * Exported for the test: the failure only shows on grouped tables, and only
 * once real data is involved, so nothing else would catch a regression here.
 */
export const buildSkeletonColumn = (column: any, index: number): any => {
  const skeleton = {
    ...column,
    // A sorter or filter on a skeleton header invites a click that would
    // reorder nothing.
    sorter: undefined,
    filters: undefined,
    onCell: undefined,
    onFilter: undefined,
    filteredValue: undefined,
  }

  if (Array.isArray(column?.children) && column.children.length) {
    return {
      ...skeleton,
      render: undefined,
      children: column.children.map(buildSkeletonColumn),
    }
  }

  return {
    ...skeleton,
    children: undefined,
    render: () => (
      <span
        className="app-table-skeleton-cell"
        // Varying the widths stops eight identical bars reading as a broken
        // rendering rather than a loading state.
        style={{ width: `${[68, 84, 55, 76, 62][index % 5]}%` }}
        aria-hidden="true"
      />
    ),
  }
}

export const resolveSkeletonRowCount = (
  dataSource: unknown,
  pagination: TablePaginationConfig | false | undefined
): number => {
  const existing = Array.isArray(dataSource) ? dataSource.length : 0

  const pageSize =
    pagination && typeof pagination === "object"
      ? pagination.pageSize ?? pagination.defaultPageSize
      : undefined

  // A refresh knows exactly how tall it is; a first load asks the page size.
  const target =
    existing > 0 ? existing : pageSize ?? APP_TABLE_DEFAULT_PAGE_SIZE

  return Math.min(Math.max(target, 1), APP_TABLE_MAX_SKELETON_ROWS)
}

/**
 * Makes a column at least as wide as its own title.
 *
 * With a scroll box the header is a separate table that copies its column
 * widths from the body, and the body sizes itself from its cells alone. A
 * column whose title is longer than anything in it ("PILON amount" over empty
 * cells) therefore came out narrower than the title, which spilled out of its
 * header cell. That left the header wider than the body, so it could be
 * scrolled sideways by itself to a place the body could not follow.
 *
 * The title is put into the first row's cell as well, with no height, so the
 * body measures it too.
 */
export const withTitleSizer = (column: any): any => {
  if (Array.isArray(column?.children) && column.children.length) {
    return { ...column, children: column.children.map(withTitleSizer) }
  }
  if (!column?.title || typeof column.title === "function") return column

  const { render, dataIndex } = column
  return {
    ...column,
    render: (value: any, record: any, index: number) => {
      const content = render ? render(value, record, index) : value
      if (index !== 0) return content
      // A render that sets cell props (colSpan and the like) is left alone.
      if (
        content &&
        typeof content === "object" &&
        "props" in content &&
        "children" in content &&
        !("type" in content)
      ) {
        return content
      }
      return (
        <>
          {content}
          <span
            className="app-table-title-sizer"
            aria-hidden="true"
          >
            {column.title}
          </span>
        </>
      )
    },
    // antd reads dataIndex to find `value`; unchanged, kept explicit for clarity.
    dataIndex,
  }
}

export function AppTable<T extends Record<string, any>>({
  pagination,
  scroll,
  tableLayout,
  styleVariant = "default",
  className,
  ...rest
}: AppTableProps<T>) {
  const defaults = resolveAppTableDefaults<T>({
    pagination,
    scroll,
    tableLayout,
  })

  // Call sites pass their own className; the design class is prepended so
  // theirs still wins on specificity ties.
  const mergedClassName = [
    styleVariant === "legacy" ? "" : "app-table",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [edges, setEdges] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  })
  // The bar spans the table container - header included, paginator excluded -
  // so it is measured rather than derived from the row-height variables.
  const [bandBox, setBandBox] = useState<{
    top: number
    height: number
  } | null>(null)

  // antd owns the scrolling element, so the affordance cannot simply wrap it:
  // .ant-table-body when scroll.y is set, .ant-table-content otherwise.
  const findScroller = useCallback(
    () =>
      wrapRef.current?.querySelector<HTMLElement>(
        ".ant-table-body, .ant-table-content"
      ) ?? null,
    []
  )

  const sync = useCallback(() => {
    const el = findScroller()
    if (!el) return

    const container = wrapRef.current?.querySelector<HTMLElement>(
      ".ant-table-container"
    )
    if (container && wrapRef.current) {
      const wrapTop = wrapRef.current.getBoundingClientRect().top
      const rect = container.getBoundingClientRect()
      const box = { top: rect.top - wrapTop, height: rect.height }
      // Commit only real changes: this runs on every scroll and resize, and a
      // fresh object each time would loop render -> effect -> setState.
      setBandBox((prev) =>
        prev && prev.top === box.top && prev.height === box.height ? prev : box
      )
    }

    const next = resolveScrollEdges({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    })
    setEdges((prev) =>
      prev.canScrollLeft === next.canScrollLeft &&
      prev.canScrollRight === next.canScrollRight
        ? prev
        : next
    )
  }, [findScroller])

  useEffect(() => {
    if (styleVariant === "legacy") return undefined
    const el = findScroller()
    if (!el) return undefined

    el.addEventListener("scroll", sync, { passive: true })
    sync()

    // Columns can widen after data arrives, changing whether there is anything
    // to scroll to without the container itself resizing.
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sync)
      observer.observe(el)
      const table = el.querySelector("table")
      if (table) observer.observe(table)
    }

    return () => {
      el.removeEventListener("scroll", sync)
      observer?.disconnect()
    }
  }, [findScroller, sync, styleVariant, rest.dataSource, rest.loading])

  const { columns, rowSelection, ...tableProps } = rest

  let orderedColumns = columns
  if (styleVariant !== "legacy") {
    orderedColumns = positionActionColumn(columns as any[]) as typeof columns
    // Only pin when the table actually scrolls sideways; on a table that fits,
    // a sticky column buys nothing and antd still forces a fixed layout.
    if (defaults.scroll && (defaults.scroll as any).x) {
      orderedColumns = freezeFirstColumn(
        orderedColumns as any[]
      ) as typeof columns
    }
  }

  // ---------------------------------------------------------------- loading --
  //
  // antd draws a centred spinner over the table while `loading` is set. On a
  // list that is nearly always the first thing a user sees, that means an empty
  // frame and a spinner: nothing communicates how much is coming, and the
  // layout jumps when rows finally arrive.
  //
  // Skeleton rows are drawn instead, in the shape of the table that is loading -
  // the real columns, at the real row height - so the page holds its size and
  // settles rather than snaps. Placed here rather than at the call sites so
  // every migrated table gets it without touching any of them.
  // antd's `loading` is `boolean | SpinProps`, and two call sites pass the
  // object form to supply an indicator. `Boolean({ spinning: false })` is
  // true, so reading it as a boolean left those tables drawing skeleton rows
  // forever. Match antd: an object means loading only when its `spinning`
  // says so, and an object without `spinning` counts as loading.
  const isLoading = resolveLoading(tableProps.loading)

  // Match the row count to what is being replaced: a refresh keeps the table
  // exactly as tall as it was, and a first load falls back to the page size so
  // the skeleton is not taller than the data can ever be.
  const skeletonRowCount = resolveSkeletonRowCount(
    tableProps.dataSource,
    defaults.pagination
  )

  const skeletonRows = Array.from({ length: skeletonRowCount }, (_, index) => ({
    __appTableSkeletonKey: `app-table-skeleton-${index}`,
  })) as unknown as readonly T[]

  // The column definitions are reused so widths, alignment and fixed columns
  // are identical to the loaded table - only the cell contents are replaced.
  const skeletonColumns = (orderedColumns as any[] | undefined)?.map(
    buildSkeletonColumn
  ) as typeof orderedColumns

  // Pin the checkbox with it, or it scrolls away from the row it belongs to.
  const mergedRowSelection =
    rowSelection && styleVariant !== "legacy" && defaults.scroll
      ? { fixed: true as const, ...rowSelection }
      : rowSelection

  const table = (
    <Table<T>
      pagination={defaults.pagination}
      scroll={defaults.scroll}
      tableLayout={defaults.tableLayout}
      className={
        [mergedClassName, isLoading ? "app-table--loading" : ""]
          .filter(Boolean)
          .join(" ") || undefined
      }
      columns={
        isLoading
          ? skeletonColumns
          : styleVariant === "legacy" || !defaults.scroll
          ? orderedColumns
          : ((orderedColumns as any[] | undefined)?.map(
              withTitleSizer
            ) as typeof orderedColumns)
      }
      rowSelection={isLoading ? undefined : mergedRowSelection}
      {...tableProps}
      {...(isLoading
        ? {
            // The spinner is what the skeleton replaces, so it is turned off
            // rather than drawn on top of it.
            loading: false,
            dataSource: skeletonRows,
            rowKey: (record: any) => record.__appTableSkeletonKey,
            // A placeholder row is not a record: clicking one, expanding it or
            // reading an empty-state message would all be wrong here.
            onRow: undefined,
            expandable: undefined,
            locale: undefined,
          }
        : {})}
    />
  )

  if (styleVariant === "legacy") return table

  const showBar = edges.canScrollRight || edges.canScrollLeft
  // One bar, never two: it sits on the side that has more content and swaps
  // over once the user reaches that edge.
  const pointsRight = edges.canScrollRight
  const barLabel = pointsRight ? "Scroll to the end" : "Scroll to the start"

  return (
    <div
      // The frozen-column seam is gated on this class: a table that does not
      // scroll sideways has nothing sliding underneath, so the divider would
      // just be an arbitrary vertical rule.
      className={`app-table-wrap${
        showBar ? " app-table-wrap--scrollable" : ""
      }`}
      ref={wrapRef}
    >
      {table}
      <AppTableScrollbar getScroller={findScroller} />
      {showBar ? (
        <Tooltip title={barLabel}>
          <button
            type="button"
            // Styling lives in Tailwind classes rather than appTable.css, mirroring
            // the payroll HorizontalScrollTable this replaces: the stylesheet is
            // injected from a lazily-loaded chunk and loses to the global reset.
            className={
              "absolute z-10 flex w-[26px] flex-col items-center justify-center " +
              "bg-[#0f172b29] px-2 text-[#0f172b] transition-colors " +
              "hover:bg-[#0f172b3d] " +
              (pointsRight ? "right-0" : "left-0")
            }
            style={
              bandBox
                ? { top: bandBox.top, height: bandBox.height }
                : { top: 0, bottom: 0 }
            }
            onClick={() => {
              const el = findScroller()
              if (el) {
                el.scrollTo({
                  left: pointsRight ? el.scrollWidth : 0,
                  behavior: "smooth",
                })
              }
            }}
            aria-label={barLabel}
          >
            <ScrollCaret direction={pointsRight ? "right" : "left"} />
          </button>
        </Tooltip>
      ) : null}
    </div>
  )
}

const ScrollCaret = ({ direction }: { direction: "left" | "right" }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 4.925 8.05"
    fill="none"
    aria-hidden="true"
    style={direction === "left" ? { transform: "rotate(180deg)" } : undefined}
  >
    <path
      d="M0.9 0.9L4.025 4.025L0.9 7.15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default AppTable
